import React, { useState, useMemo, useContext, useRef, useEffect } from 'react';
import AppContext from '../../contexts/AppContext';
import { MessageCircle, X, Heart, Send, Users } from 'lucide-react';
import * as firestoreService from '../../services/firestoreService';
import SecurityUtils from '../../utils/security';
import { getRelativeTime } from '../../utils/formatting';
import { applyWordFilter } from '../../utils/content';
import { REACTIONS, getUserReactionType, getReactionTotal } from '../../utils/reactions';
import Avatar from '../../components/common/Avatar';
import ReportModal from '../../components/moderation/ReportModal';
import PostCard from './PostCard';
import SwingBar from './SwingBar';
import SwingViewer from './SwingViewer';
import SwingCreateModal from './SwingCreateModal';

const FeedView = () => {
  const {
    posts, users, currentUser, setPosts,
    viewUserProfile, setView,
    conversations, setConversations, messages, setMessages,
    toggleFollow, wordFilters, showAlert, setSearchNav,
    swings, setSwings, handleSwingReply,
  } = useContext(AppContext);

  // ── Tab / scroll ──────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState('feed');
  const [tabsVisible, setTabsVisible] = useState(true);
  const lastScrollY = useRef(0);
  const feedRef = useRef(null);

  useEffect(() => {
    const scrollEl = feedRef.current?.closest('[class*="overflow-y-auto"]') || document.querySelector('main');
    if (!scrollEl) return;
    const onScroll = () => {
      const y = scrollEl.scrollTop;
      if (y < 60) setTabsVisible(true);
      else if (y < lastScrollY.current) setTabsVisible(true);
      else if (y > lastScrollY.current + 4) setTabsVisible(false);
      lastScrollY.current = y;
    };
    scrollEl.addEventListener('scroll', onScroll, { passive: true });
    return () => scrollEl.removeEventListener('scroll', onScroll);
  }, []);

  // ── Pagination ────────────────────────────────────────────────────────────
  const [displayedCount, setDisplayedCount] = useState(10);
  const loadMoreRef = useRef(null);

  useEffect(() => {
    const el = loadMoreRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting) setDisplayedCount(prev => prev + 10);
    }, { threshold: 0.1 });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // ── Not interested signals ────────────────────────────────────────────────
  const [notInterestedSignals, setNotInterestedSignals] = useState(() => {
    try { return JSON.parse(localStorage.getItem('loft_not_interested') || '[]'); } catch { return []; }
  });

  const handleNotInterested = (post) => {
    const updated = [
      ...notInterestedSignals.filter(s => s.userId !== post.userId),
      { userId: post.userId, timestamp: Date.now() },
    ].slice(-50); // keep last 50 signals
    setNotInterestedSignals(updated);
    localStorage.setItem('loft_not_interested', JSON.stringify(updated));
  };

  // ── Swings ────────────────────────────────────────────────────────────────
  const [swingViewerUserId, setSwingViewerUserId] = useState(null); // open viewer on this userId
  const [swingCreateOpen, setSwingCreateOpen] = useState(false);

  // Ordered list of userIds for the SwingBar/Viewer (current user first, then unseen-first others)
  const swingUserOrder = useMemo(() => {
    const byUser = {};
    swings.forEach(s => {
      if (!byUser[s.userId]) byUser[s.userId] = [];
      byUser[s.userId].push(s);
    });
    const mySwings = byUser[currentUser.id];
    const others = Object.keys(byUser).filter(uid => uid !== currentUser.id);
    const hasUnseen = uid => (byUser[uid] || []).some(s => !(s.viewedBy || []).includes(currentUser.id));
    others.sort((a, b) => {
      if (hasUnseen(a) !== hasUnseen(b)) return hasUnseen(a) ? -1 : 1;
      const aLatest = (byUser[a] || []).reduce((l, s) => s.createdAt > l ? s.createdAt : l, '');
      const bLatest = (byUser[b] || []).reduce((l, s) => s.createdAt > l ? s.createdAt : l, '');
      return bLatest.localeCompare(aLatest);
    });
    return [...(mySwings ? [currentUser.id] : []), ...others];
  }, [swings, currentUser.id]);

  const handleDeleteSwing = (swingId) => {
    setSwings(prev => prev.filter(s => s.id !== swingId));
    firestoreService.deleteSwing(swingId).catch(err => console.error('Error deleting swing:', err));
  };

  // ── Post handlers ─────────────────────────────────────────────────────────
  const [editingPost, setEditingPost] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const handleEditPost = (postId) => {
    const post = posts.find(p => p.id === postId);
    if (post) { setEditingPost(postId); setEditContent(post.content); }
  };

  const handleSaveEdit = (postId) => {
    if (!editContent.trim()) { showAlert('Post content cannot be empty', 'Error'); return; }
    const sanitized = SecurityUtils.sanitizeText(editContent);
    setPosts(posts.map(p => p.id === postId ? { ...p, content: sanitized, edited: true, editedAt: new Date().toISOString() } : p));
    setEditingPost(null);
    setEditContent('');
    firestoreService.updatePost(postId, { content: sanitized, edited: true, editedAt: new Date().toISOString() }).catch(err => console.error('Error updating post:', err));
  };

  const handleDeletePost = (postId) => setDeleteConfirm(postId);

  const confirmDelete = () => {
    const postId = deleteConfirm;
    setPosts(posts.filter(p => p.id !== postId));
    setDeleteConfirm(null);
    firestoreService.deletePost(postId).catch(err => console.error('Error deleting post:', err));
  };

  // ── Reactions ─────────────────────────────────────────────────────────────
  const handleReaction = async (postId, reactionType) => {
    const post = posts.find(p => p.id === postId);
    try {
      const result = await firestoreService.toggleReaction(postId, currentUser.id, reactionType);
      setPosts(posts.map(p => p.id === postId ? { ...p, reactions: result.reactions } : p));
      if (!result.wasRemoved && post && post.userId !== currentUser.id) {
        firestoreService.createNotification({
          targetUserId: post.userId,
          fromUserId: currentUser.id,
          type: 'reaction',
          message: `liked your post`,
          postId,
        }).catch(err => console.error('Error creating reaction notification:', err));
      }
    } catch (err) { console.error('Error reacting to post:', err); }
  };

  // ── Save / bookmark ───────────────────────────────────────────────────────
  const handleSavePost = (postId) => {
    setPosts(posts.map(p => {
      if (p.id !== postId) return p;
      const saved = (p.saves || []).includes(currentUser.id);
      return { ...p, saves: saved ? p.saves.filter(id => id !== currentUser.id) : [...(p.saves || []), currentUser.id] };
    }));
    firestoreService.savePost(postId, currentUser.id).catch(err => console.error('Error saving post:', err));
  };

  // ── Comments ──────────────────────────────────────────────────────────────
  // handleAddComment is called from PostCard inline input AND the comment modal
  const handleAddComment = async (postId, content, replyToObj) => {
    if (!content?.trim()) return;
    const sanitized = SecurityUtils.sanitizeText(content);
    const replyFields = replyToObj
      ? { replyToCommentId: replyToObj.commentId, replyToUserId: replyToObj.userId }
      : {};
    try {
      await firestoreService.addComment(postId, { userId: currentUser.id, content: sanitized, likes: [], ...replyFields });
      const post = posts.find(p => p.id === postId);
      if (post && post.userId !== currentUser.id) {
        firestoreService.createNotification({
          targetUserId: post.userId,
          fromUserId: currentUser.id,
          type: 'comment',
          message: `commented on your post: "${sanitized.substring(0, 50)}${sanitized.length > 50 ? '...' : ''}"`,
          postId,
        }).catch(err => console.error('Error creating comment notification:', err));
      }
      const mentionRegex = /@(\w+)/g;
      const mentionedHandles = new Set();
      let match;
      while ((match = mentionRegex.exec(sanitized)) !== null) mentionedHandles.add(match[1].toLowerCase());
      mentionedHandles.forEach(handle => {
        firestoreService.getUserByHandle(handle).then(mentionedUser => {
          if (
            mentionedUser &&
            mentionedUser.id !== currentUser.id &&
            mentionedUser.id !== post?.userId &&
            mentionedUser.role !== 'admin' &&
            mentionedUser.role !== 'superadmin'
          ) {
            return firestoreService.createNotification({
              targetUserId: mentionedUser.id,
              fromUserId: currentUser.id,
              type: 'comment',
              message: `mentioned you in a comment: "${sanitized.substring(0, 50)}${sanitized.length > 50 ? '...' : ''}"`,
              postId,
            });
          }
        }).catch(err => console.error('Error creating mention notification:', err));
      });
    } catch (err) { console.error('Error adding comment:', err); }
  };

  const handleLikeComment = (postId, commentId) => {
    const post = posts.find(p => p.id === postId);
    if (!post) return;
    const updatedComments = post.comments.map(c => {
      if (c.id !== commentId) return c;
      const liked = (c.likes || []).includes(currentUser.id);
      return { ...c, likes: liked ? (c.likes || []).filter(id => id !== currentUser.id) : [...(c.likes || []), currentUser.id] };
    });
    setPosts(posts.map(p => p.id === postId ? { ...p, comments: updatedComments } : p));
    firestoreService.updatePost(postId, { comments: updatedComments }).catch(err => console.error('Error liking comment:', err));
    const comment = post.comments.find(c => c.id === commentId);
    const alreadyLiked = (comment?.likes || []).includes(currentUser.id);
    if (comment && !alreadyLiked && comment.userId !== currentUser.id) {
      firestoreService.createNotification({
        targetUserId: comment.userId,
        fromUserId: currentUser.id,
        type: 'comment_like',
        message: 'liked your comment',
        postId,
      }).catch(err => console.error('Error creating comment like notification:', err));
    }
  };

  const handleDeleteComment = (postId, commentId) => {
    const post = posts.find(p => p.id === postId);
    if (!post) return;
    const updatedComments = post.comments.filter(c => c.id !== commentId && c.replyToCommentId !== commentId);
    setPosts(posts.map(p => p.id === postId ? { ...p, comments: updatedComments } : p));
    firestoreService.updatePost(postId, { comments: updatedComments }).catch(err => console.error('Error deleting comment:', err));
  };

  // ── Share ─────────────────────────────────────────────────────────────────
  const [showShareModal, setShowShareModal] = useState(null);

  const getMutualFriends = () =>
    users.filter(u =>
      u.id !== currentUser.id &&
      currentUser.following?.includes(u.id) &&
      u.following?.includes(currentUser.id)
    );

  const handleSharePost = (postId, recipientId) => {
    const post = posts.find(p => p.id === postId);
    if (!post) return;
    const author = users.find(u => u.id === post.userId);
    const postPreview = post.content.substring(0, 100) + (post.content.length > 100 ? '...' : '');
    const shareMessage = `${currentUser.name} shared a post from ${author?.name}`;
    let conversation = conversations.find(conv =>
      conv.participants.includes(currentUser.id) && conv.participants.includes(recipientId)
    );
    if (!conversation) {
      conversation = { id: `conv${Date.now()}`, participants: [currentUser.id, recipientId], lastMessage: shareMessage, lastMessageTime: new Date().toISOString() };
      setConversations([...conversations, conversation]);
    } else {
      setConversations(conversations.map(conv => conv.id === conversation.id ? { ...conv, lastMessage: shareMessage, lastMessageTime: new Date().toISOString() } : conv));
    }
    const message = {
      id: `m${Date.now()}`,
      conversationId: conversation.id,
      senderId: currentUser.id,
      content: shareMessage,
      timestamp: new Date().toISOString(),
      read: false,
      sharedPostId: postId,
      sharedPostPreview: postPreview,
      sharedPostAuthor: author?.name,
    };
    setMessages([...messages, message]);
    setShowShareModal(null);
    firestoreService.createNotification({
      targetUserId: recipientId,
      fromUserId: currentUser.id,
      type: 'share',
      message: `shared a post with you from ${author?.name || 'a user'}`,
      postId,
    }).catch(err => console.error('Error creating share notification:', err));
  };

  // ── Report / modals ───────────────────────────────────────────────────────
  const [showReportModal, setShowReportModal] = useState(null);
  const [commentModal, setCommentModal] = useState(null);

  // Comment modal local state
  const [modalComment, setModalComment] = useState('');
  const [modalReplyTo, setModalReplyTo] = useState(null);
  const [modalMentionSuggestions, setModalMentionSuggestions] = useState([]);
  const [modalMentionActive, setModalMentionActive] = useState(false);
  const modalCommentRef = useRef(null);
  const modalCommentInputRefs = useRef({});

  const checkModalMention = (value, cursorPos) => {
    const before = value.slice(0, cursorPos);
    const match = before.match(/@(\w+)$/);
    if (match) {
      const q = match[1].toLowerCase();
      setModalMentionSuggestions(
        users.filter(u => u.handle && u.role !== 'admin' && u.role !== 'superadmin' && u.handle.toLowerCase().startsWith(q)).slice(0, 8)
      );
      setModalMentionActive(true);
    } else {
      setModalMentionSuggestions([]);
      setModalMentionActive(false);
    }
  };

  const applyModalMention = (handle) => {
    const el = commentModal ? modalCommentInputRefs.current[`modal-${commentModal}`] : null;
    const cursor = el ? el.selectionStart : modalComment.length;
    const before = modalComment.slice(0, cursor).replace(/@\w+$/, `@${handle} `);
    const after = modalComment.slice(cursor);
    setModalComment(before + after);
    setModalMentionSuggestions([]);
    setModalMentionActive(false);
    setTimeout(() => { if (el) { el.focus(); el.setSelectionRange(before.length, before.length); } }, 0);
  };

  const handleModalSubmitComment = () => {
    if (!modalComment.trim()) return;
    handleAddComment(commentModal, modalComment, modalReplyTo);
    setModalComment('');
    setModalReplyTo(null);
    setModalMentionSuggestions([]);
    setModalMentionActive(false);
  };

  // ── Feed ranking & filtering ──────────────────────────────────────────────
  const scoreFeedPost = (post) => {
    const ageHours = (Date.now() - new Date(post.timestamp).getTime()) / 3600000;
    const engagement = getReactionTotal(post) * 3 + (post.comments?.length || 0) * 4 + (post.saves?.length || 0) * 2 + (post.shares || 0);
    const followBoost = currentUser.following?.includes(post.userId) ? 15 : 0;
    const ownBoost = post.userId === currentUser.id ? 5 : 0;
    const recencyScore = 100 / (1 + ageHours / 12);
    const notInterestedPenalty = notInterestedSignals.some(s => s.userId === post.userId) ? 80 : 0;
    return recencyScore + engagement + followBoost + ownBoost - notInterestedPenalty;
  };

  const blockedUsers = currentUser.blockedUsers || [];
  const postIds = posts.map(p => p.id).join(',');

  const sortedPostOrder = useMemo(() => {
    const eligible = posts.filter(post =>
      !blockedUsers.includes(post.userId) &&
      (post.type === 'general' || post.type === 'lookingToPlay' || post.type === 'roundRecap' || post.type === 'gameRecap')
    );
    return eligible.sort((a, b) => scoreFeedPost(b) - scoreFeedPost(a)).map(p => p.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postIds, activeTab]);

  const filteredPosts = sortedPostOrder
    .map(id => posts.find(p => p.id === id))
    .filter(post => {
      if (!post) return false;
      if (blockedUsers.includes(post.userId)) return false;
      if (activeTab === 'following' && !currentUser.following?.includes(post.userId)) return false;
      return true;
    });

  const displayedPosts = filteredPosts.slice(0, displayedCount);

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div ref={feedRef} className="max-w-2xl mx-auto">

      {/* ── Sticky header: SwingBar + tabs ── */}
      <div
        className="bg-black sticky top-0 z-10 transition-transform duration-300"
        style={{ transform: tabsVisible ? 'translateY(0)' : 'translateY(-110%)' }}
      >
        {/* Swing bar — only shown when there's something to see or the user can create */}
        <SwingBar
          swings={swings}
          currentUser={currentUser}
          users={users}
          swingUserOrder={swingUserOrder}
          onOpenViewer={uid => setSwingViewerUserId(uid)}
          onOpenCreate={() => setSwingCreateOpen(true)}
        />

        {/* Tab bar */}
        <div className="border-b border-gray-800 flex">
          <button
            onClick={() => setActiveTab('feed')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === 'feed' ? 'text-white border-b-2 border-white font-bold' : 'text-gray-400 hover:text-white'
            }`}
          >
            All Posts
          </button>
          <button
            onClick={() => setActiveTab('following')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === 'following' ? 'text-white border-b-2 border-white font-bold' : 'text-gray-400 hover:text-white'
            }`}
          >
            Following
          </button>
        </div>
      </div> {/* end sticky header */}

      {/* ── Post list ── */}
      {filteredPosts.length === 0 ? (
        <div className="p-8 text-center">
          <Users className="w-12 h-12 text-gray-600 mx-auto mb-3" />
          <h3 className="text-lg font-medium text-white mb-2">No posts yet</h3>
          <p className="text-gray-500">
            {activeTab === 'following' ? 'Follow other golfers to see their posts here!' : 'No posts available. Create one to get started!'}
          </p>
        </div>
      ) : (
        <>
          {displayedPosts.map(post => {
            const author = users.find(u => u.id === post.userId);
            if (!author) return null;
            return (
              <PostCard
                key={post.id}
                post={post}
                author={author}
                currentUser={currentUser}
                users={users}
                wordFilters={wordFilters}
                isEditing={editingPost === post.id}
                editContent={editContent}
                onEditChange={setEditContent}
                onSaveEdit={handleSaveEdit}
                onCancelEdit={() => { setEditingPost(null); setEditContent(''); }}
                onReaction={handleReaction}
                onSave={handleSavePost}
                onAddComment={handleAddComment}
                onLikeComment={handleLikeComment}
                onDeleteComment={handleDeleteComment}
                onEdit={handleEditPost}
                onDelete={handleDeletePost}
                onReport={(postId, userId) => setShowReportModal({ type: 'post', targetId: postId, targetUserId: userId })}
                onShare={setShowShareModal}
                onViewProfile={viewUserProfile}
                onHashtag={tag => { if (setSearchNav) setSearchNav({ tab: 'posts', query: tag }); setView('search'); }}
                onOpenComments={postId => { setCommentModal(postId); setModalComment(''); setModalReplyTo(null); }}
                toggleFollow={toggleFollow}
              />
            );
          })}

          {/* Load-more sentinel */}
          {filteredPosts.length > displayedCount && (
            <div ref={loadMoreRef} className="py-10 flex justify-center">
              <div className="w-6 h-6 border-2 border-[#16a34a] border-t-transparent rounded-full animate-spin" />
            </div>
          )}
        </>
      )}

      {/* ── Delete confirm ── */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-gray-700 rounded-xl p-6 max-w-sm w-full">
            <h3 className="text-lg font-bold text-white mb-2">Delete Post?</h3>
            <p className="text-gray-400 mb-6">Are you sure you want to delete this post? This cannot be undone.</p>
            <div className="flex space-x-3">
              <button onClick={() => setDeleteConfirm(null)} className="flex-1 py-2 border border-gray-700 text-white rounded-lg hover:bg-gray-800">Cancel</button>
              <button onClick={confirmDelete} className="flex-1 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-bold">Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Report modal ── */}
      {showReportModal && (
        <ReportModal
          onClose={() => setShowReportModal(null)}
          reportType={showReportModal.type}
          targetId={showReportModal.targetId}
          targetUserId={showReportModal.targetUserId}
        />
      )}

      {/* ── Share modal ── */}
      {showShareModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-gray-700 rounded-xl p-6 max-w-md w-full max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-white">Share Post</h3>
              <button onClick={() => setShowShareModal(null)} className="text-gray-400 hover:text-white"><X className="w-6 h-6" /></button>
            </div>
            {getMutualFriends().length === 0 ? (
              <div className="text-center py-8">
                <Users className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-400">No mutual friends to share with</p>
                <p className="text-sm text-gray-500 mt-2">Follow others who follow you back to share posts.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {getMutualFriends().map(user => (
                  <button key={user.id} onClick={() => handleSharePost(showShareModal, user.id)} className="w-full flex items-center space-x-3 p-3 hover:bg-gray-800 rounded-lg transition-colors">
                    <Avatar user={user} size="md" />
                    <div className="flex-1 text-left">
                      <p className="font-medium text-white">{user.name}</p>
                      <p className="text-sm text-gray-400">@{user.handle}</p>
                    </div>
                    <Send className="w-5 h-5 text-gray-400" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Swing viewer ── */}
      {swingViewerUserId && swingUserOrder.length > 0 && (
        <SwingViewer
          userOrder={swingUserOrder}
          initialUserId={swingViewerUserId}
          swings={swings}
          currentUser={currentUser}
          users={users}
          onClose={() => setSwingViewerUserId(null)}
          onReply={handleSwingReply}
          onDelete={handleDeleteSwing}
        />
      )}

      {/* ── Swing create modal ── */}
      {swingCreateOpen && (
        <SwingCreateModal onClose={() => setSwingCreateOpen(false)} />
      )}

      {/* ── Comment modal ── */}
      {commentModal && (() => {
        const post = posts.find(p => p.id === commentModal);
        if (!post) return null;
        const author = users.find(u => u.id === post.userId);
        const topLevel = post.comments.filter(c => !c.replyToCommentId);
        const repliesList = post.comments.filter(c => c.replyToCommentId);
        return (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-end sm:items-center justify-center" onClick={() => { setCommentModal(null); setModalReplyTo(null); setModalComment(''); }}>
            <div className="bg-black w-full sm:max-w-lg sm:rounded-xl max-h-[90vh] flex flex-col border border-gray-800" onClick={e => e.stopPropagation()}>

              {/* Modal header */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800">
                <h3 className="text-base font-bold text-white">Comments</h3>
                <button onClick={() => { setCommentModal(null); setModalReplyTo(null); setModalComment(''); }} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
              </div>

              {/* Post snippet */}
              <div className="px-4 py-3 border-b border-gray-800 flex items-start space-x-3">
                <Avatar user={author} size="sm" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white">
                    <button onClick={() => { setCommentModal(null); viewUserProfile(author?.id); }} className="font-bold text-white hover:text-gray-300 mr-1">{author?.handle || 'user'}</button>
                    {post.content.length > 120 ? post.content.substring(0, 120) + '...' : post.content}
                  </p>
                  <span className="text-xs text-gray-500">{getRelativeTime(post.timestamp)}</span>
                </div>
              </div>

              {/* Comments list */}
              <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
                {post.comments.length === 0 ? (
                  <div className="text-center py-8">
                    <MessageCircle className="w-10 h-10 text-gray-600 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">No comments yet. Be the first!</p>
                  </div>
                ) : topLevel.map(comment => {
                  const commenter = users.find(u => u.id === comment.userId);
                  const commentReplies = repliesList.filter(r => r.replyToCommentId === comment.id);
                  const commentLiked = (comment.likes || []).includes(currentUser.id);
                  return (
                    <div key={comment.id}>
                      <div className="flex items-start space-x-3">
                        <Avatar user={commenter} size="sm" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-white">
                            <button onClick={() => { setCommentModal(null); viewUserProfile(commenter?.id); }} className="font-bold text-white hover:text-gray-300 mr-1">{commenter?.handle || 'user'}</button>
                            {applyWordFilter(comment.content, wordFilters)}
                          </p>
                          <div className="flex items-center space-x-3 mt-1">
                            <span className="text-xs text-gray-500">{getRelativeTime(comment.timestamp)}</span>
                            {(comment.likes || []).length > 0 && (
                              <span className="text-xs text-gray-500 font-bold">{(comment.likes || []).length} {(comment.likes || []).length === 1 ? 'like' : 'likes'}</span>
                            )}
                            <button onClick={() => { setModalReplyTo({ postId: post.id, commentId: comment.id, userId: comment.userId, handle: commenter?.handle }); setModalComment(`@${commenter?.handle} `); }} className="text-xs text-gray-500 font-bold hover:text-white">Reply</button>
                            {(comment.userId === currentUser.id || post.userId === currentUser.id) && (
                              <button onClick={() => handleDeleteComment(post.id, comment.id)} className="text-xs text-gray-500 hover:text-red-500">Delete</button>
                            )}
                          </div>
                        </div>
                        <button onClick={() => handleLikeComment(post.id, comment.id)} className="mt-1 flex-shrink-0">
                          <Heart className={`w-3.5 h-3.5 ${commentLiked ? 'fill-red-500 text-red-500' : 'text-gray-500 hover:text-white'}`} />
                        </button>
                      </div>
                      {commentReplies.length > 0 && (
                        <div className="ml-10 mt-2 space-y-2">
                          {commentReplies.map(reply => {
                            const replier = users.find(u => u.id === reply.userId);
                            const replyLiked = (reply.likes || []).includes(currentUser.id);
                            return (
                              <div key={reply.id} className="flex items-start space-x-3">
                                <Avatar user={replier} size="xs" />
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm text-white">
                                    <button onClick={() => { setCommentModal(null); viewUserProfile(replier?.id); }} className="font-bold text-white hover:text-gray-300 mr-1">{replier?.handle || 'user'}</button>
                                    {applyWordFilter(reply.content, wordFilters)}
                                  </p>
                                  <div className="flex items-center space-x-3 mt-0.5">
                                    <span className="text-xs text-gray-500">{getRelativeTime(reply.timestamp)}</span>
                                    {(reply.likes || []).length > 0 && <span className="text-xs text-gray-500 font-bold">{(reply.likes || []).length} {(reply.likes || []).length === 1 ? 'like' : 'likes'}</span>}
                                    <button onClick={() => { setModalReplyTo({ postId: post.id, commentId: comment.id, userId: reply.userId, handle: replier?.handle }); setModalComment(`@${replier?.handle} `); }} className="text-xs text-gray-500 font-bold hover:text-white">Reply</button>
                                    {(reply.userId === currentUser.id || post.userId === currentUser.id) && (
                                      <button onClick={() => handleDeleteComment(post.id, reply.id)} className="text-xs text-gray-500 hover:text-red-500">Delete</button>
                                    )}
                                  </div>
                                </div>
                                <button onClick={() => handleLikeComment(post.id, reply.id)} className="mt-1 flex-shrink-0">
                                  <Heart className={`w-3 h-3 ${replyLiked ? 'fill-red-500 text-red-500' : 'text-gray-500 hover:text-white'}`} />
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Modal comment input */}
              <div className="relative">
                <div className="px-4 py-3 border-t border-gray-800 flex items-center space-x-2">
                  <Avatar user={currentUser} size="sm" />
                  {modalReplyTo && (
                    <button onClick={() => { setModalReplyTo(null); setModalComment(''); }} className="text-gray-500 hover:text-white"><X className="w-3 h-3" /></button>
                  )}
                  <input
                    ref={el => { modalCommentInputRefs.current[`modal-${post.id}`] = el; }}
                    type="text"
                    value={modalComment}
                    onChange={e => { setModalComment(e.target.value); checkModalMention(e.target.value, e.target.selectionStart); }}
                    onBlur={() => setTimeout(() => { setModalMentionSuggestions([]); setModalMentionActive(false); }, 150)}
                    onKeyPress={e => e.key === 'Enter' && handleModalSubmitComment()}
                    placeholder={modalReplyTo ? `Reply to @${modalReplyTo.handle}...` : 'Add a comment...'}
                    className="flex-1 text-sm text-white bg-transparent outline-none placeholder-gray-600"
                    autoFocus
                  />
                  {modalComment.trim() && (
                    <button onClick={handleModalSubmitComment} className="text-sm font-bold text-[#16a34a] hover:text-[#15803d]">Post</button>
                  )}
                </div>
                {modalMentionActive && modalMentionSuggestions.length > 0 && (
                  <div className="absolute bottom-full left-0 right-0 z-50 bg-gray-900 border border-gray-700 rounded-lg overflow-hidden shadow-xl mb-1">
                    {modalMentionSuggestions.map(u => (
                      <button key={u.id} onMouseDown={e => { e.preventDefault(); applyModalMention(u.handle); }} className="w-full text-left px-3 py-2 flex items-center space-x-3 hover:bg-gray-800">
                        <Avatar user={u} size="sm" />
                        <div>
                          <p className="text-sm font-bold text-white">{u.name}</p>
                          <p className="text-xs text-gray-400">@{u.handle}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default FeedView;
